/**
 * MetricsTracker - Unified stream metrics tracking
 * 
 * Provides consistent metric tracking across all platforms.
 * Tracks incremental and absolute metrics from stream events.
 */

export class MetricsTracker {
  constructor() {
    this.metrics = {
      likeCount: 0,
      giftCount: 0,
      totalGiftValue: 0,
      shareCount: 0,
      commentCount: 0,
      viewerCount: 0,
      totalViewers: 0,
      followerCount: 0
    };
  }

  /**
   * Initialize metrics with baseline values from room data
   * @param {object} roomData - Initial room data from platform
   */
  initializeMetrics(roomData) {
    if (!roomData) return;

    // TikTok room data structure
    const viewerCount = roomData.user_count || roomData.stats?.user_count || roomData.viewerCount || 0;
    const likeCount = roomData.like_count || roomData.stats?.like_count || roomData.likeCount || 0;
    const totalViewers = roomData.total_user || roomData.stats?.total_user || roomData.totalViewers || 0;
    const followerCount = roomData.follower_count || roomData.stats?.follower_count || roomData.followerCount || 0;

    // Only update if values are higher (don't go backwards)
    if (viewerCount > this.metrics.viewerCount) {
      this.metrics.viewerCount = viewerCount;
    }
    if (likeCount > this.metrics.likeCount) {
      this.metrics.likeCount = likeCount;
    }
    if (totalViewers > this.metrics.totalViewers) {
      this.metrics.totalViewers = totalViewers;
    }
    if (followerCount > this.metrics.followerCount) {
      this.metrics.followerCount = followerCount;
    }

    console.log('[MetricsTracker] Initialized metrics:', this.metrics);
  }

  /**
   * Process a normalized event and update metrics
   * @param {object} normalizedEvent - Normalized event from parser
   * @returns {{ metricsChanged: boolean, currentMetrics: object }}
   */
  processMessage(normalizedEvent) {
    if (!normalizedEvent) {
      return { metricsChanged: false, currentMetrics: this.metrics };
    }

    let changed = false;

    switch (normalizedEvent.type) {
      case 'comment':
        this.metrics.commentCount++;
        changed = true;
        break;

      case 'gift':
        // Increment by gift count (for multi-gift sends)
        const giftCount = normalizedEvent.giftCount || 1;
        this.metrics.giftCount += giftCount;
        
        // Track total gift value (diamonds/coins)
        const giftValue = normalizedEvent.totalValue || (normalizedEvent.value || 0) * giftCount;
        this.metrics.totalGiftValue += giftValue;
        
        changed = true;
        break;

      case 'like':
        // Normalized event structure: { type: 'like', totalLikeCount: 5258, likeCount: 2, ... }
        // Fields are on the event object directly, NOT inside .data
        if (normalizedEvent.totalLikeCount > 0) {
          this.metrics.likeCount = normalizedEvent.totalLikeCount;
          changed = true;
        } else {
          // Fallback for platforms without totalLikeCount
          const incrementalLikes = normalizedEvent.likeCount || normalizedEvent.count || 1;
          this.metrics.likeCount += incrementalLikes;
          changed = true;
        }
        break;

      case 'share':
        // TikTok provides absolute total shareCount in WebcastSocialMessage
        const totalShareCount = normalizedEvent.shareCount;
        if (totalShareCount !== undefined && totalShareCount >= 0) {
          // Always use the absolute total from TikTok (most accurate)
          this.metrics.shareCount = totalShareCount;
          changed = true;
        } else {
          // Fallback to increment for other platforms
          this.metrics.shareCount++;
          changed = true;
        }
        break;

      case 'join':
        // Join events increment current viewer count
        this.metrics.viewerCount++;
        changed = true;
        break;

      case 'follow':
        // TikTok provides absolute total followCount in WebcastSocialMessage
        const totalFollowCount = normalizedEvent.followCount;
        if (totalFollowCount !== undefined && totalFollowCount >= 0) {
          // Always use the absolute total from TikTok (most accurate)
          this.metrics.followerCount = totalFollowCount;
          changed = true;
        } else {
          // Fallback to increment for other platforms
          this.metrics.followerCount++;
          changed = true;
        }
        break;

      default:
        // Unknown event type, no metric changes
        break;
    }

    // Check if event contains periodic metric updates (like WebcastRoomUserSeqMessage)
    if (normalizedEvent.data?.viewerCount !== undefined) {
      const newViewerCount = normalizedEvent.data.viewerCount;
      if (newViewerCount !== this.metrics.viewerCount) {
        this.metrics.viewerCount = newViewerCount;
        changed = true;
      }
    }

    if (normalizedEvent.data?.totalViewers !== undefined) {
      const newTotalViewers = normalizedEvent.data.totalViewers;
      if (newTotalViewers > this.metrics.totalViewers) {
        this.metrics.totalViewers = newTotalViewers;
        changed = true;
      }
    }

    return {
      metricsChanged: changed,
      currentMetrics: { ...this.metrics }
    };
  }

  /**
   * Get current metrics state
   * @returns {object} Current metrics
   */
  getCurrentMetrics() {
    return { ...this.metrics };
  }

  /**
   * Reset all metrics to zero (on disconnect)
   */
  reset() {
    this.metrics = {
      likeCount: 0,
      giftCount: 0,
      totalGiftValue: 0,
      shareCount: 0,
      commentCount: 0,
      viewerCount: 0,
      totalViewers: 0,
      followerCount: 0
    };
    console.log('[MetricsTracker] Reset metrics');
  }
}
